import { ticketingSalesQueryKeys } from "../query-keys/event-ticketing-sales.query-keys";
import { privateAxios } from "../axiosInstances";
import { handleApiCall } from "../api-error-handler";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { ordersQueryKeys } from "../query-keys";
import { creditNotesQueryKeys } from "../query-keys/credit-note.query-keys";
import {
  BulkCreateCreditNotePayload,
  BulkCreateCreditNoteResult,
  CreateAcExpoCreditNotePayload,
  CreateAcExpoCreditNoteResponse,
} from "@ac-expo/api-types";

export type UpdateCreditNotePayload = {
  creditNoteId: string;
  items: {
    code: string;
    type: "ticket" | "badge";
  }[];
};

const updateCreditNote = async (payload: UpdateCreditNotePayload) => {
  const { creditNoteId, ...rest } = payload;
  const { data } = await privateAxios.patch(
    `/credit-notes/${creditNoteId}`,
    rest
  );
  return data;
};

export const useUpdateCreditNoteMutation = (eventId?: string) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: updateCreditNote,
    onSuccess: () => {
      if (eventId) {
        queryClient.invalidateQueries({
          queryKey: ticketingSalesQueryKeys.event(eventId),
        });
        queryClient.invalidateQueries({
          queryKey: ordersQueryKeys.exhibitorEventSales(eventId),
        });
      }
    },
  });
};

export type CreateCreditNotePayload = {
  orderId: string;
  items: {
    code: string;
    type: "ticket" | "badge";
  }[];
};

const createCreditNote = async (payload: CreateCreditNotePayload) => {
  const { data } = await privateAxios.post("/credit-notes", payload);
  return data;
};

export const useCreateCreditNoteMutation = (eventId?: string) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: createCreditNote,
    onSuccess: () => {
      if (eventId) {
        queryClient.invalidateQueries({
          queryKey: ticketingSalesQueryKeys.event(eventId),
        });
        queryClient.invalidateQueries({
          queryKey: ordersQueryKeys.exhibitorEventSales(eventId),
        });
      }
    },
  });
};

export type BulkUpdateCreditNoteStatusPayload = {
  creditNoteIds: string[];
  status: "accepted" | "refused";
};

export type BulkUpdateCreditNoteStatusResponse = {
  updatedIds: string[];
  failed: { creditNoteId: string; error: string }[];
};

const bulkUpdateCreditNoteStatus = async (
  payload: BulkUpdateCreditNoteStatusPayload
) => {
  const { data } = await privateAxios.patch<BulkUpdateCreditNoteStatusResponse>(
    "/credit-notes/status/bulk",
    payload
  );
  return data;
};

export const useBulkUpdateCreditNoteStatusMutation = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: bulkUpdateCreditNoteStatus,
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: creditNotesQueryKeys.all,
      });
    },
  });
};

export type ReAddRefundItemToBasketPayload = {
  creditNoteId: string;
};

export type ReAddRefundItemToBasketResponse = {
  added: boolean;
};

const reAddRefundItemToBasket = async ({
  creditNoteId,
}: ReAddRefundItemToBasketPayload) => {
  const { data } = await privateAxios.post<ReAddRefundItemToBasketResponse>(
    `/credit-notes/${creditNoteId}/basket/refund`
  );
  return data;
};

export const useReAddRefundItemToBasketMutation = (eventId?: string) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: reAddRefundItemToBasket,
    onSuccess: () => {
      if (eventId) {
        queryClient.invalidateQueries({
          queryKey: ticketingSalesQueryKeys.event(eventId),
        });
        queryClient.invalidateQueries({
          queryKey: ordersQueryKeys.exhibitorEventSales(eventId),
        });
      }
    },
  });
};

const bulkCreateCreditNotes = (payload: BulkCreateCreditNotePayload) =>
  handleApiCall(() =>
    privateAxios.post<BulkCreateCreditNoteResult>("/credit-notes/bulk", payload)
  );

export const useBulkCreateCreditNotesMutation = (eventId: string) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: bulkCreateCreditNotes,
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ticketingSalesQueryKeys.event(eventId),
      });
      queryClient.invalidateQueries({
        queryKey: ordersQueryKeys.exhibitorEventSales(eventId),
      });
    },
  });
};

const createAcExpoCreditNote = async (payload: CreateAcExpoCreditNotePayload) => {
  const { data } = await privateAxios.post<CreateAcExpoCreditNoteResponse>(
    "/credit-notes/ac-expo",
    payload
  );
  return data;
};

export const useCreateAcExpoCreditNoteMutation = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: createAcExpoCreditNote,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: creditNotesQueryKeys.all });
      queryClient.invalidateQueries({ queryKey: creditNotesQueryKeys.acExpo.all });
    },
  });
};
